import { Platform } from 'react-native';
import * as Location from 'expo-location';
import * as TaskManager from 'expo-task-manager';
import { api } from '../api';
import { t } from '../i18n';

/**
 * Driver location while an order is "out for delivery" (seen by the admin only).
 * Phones: a background location task with Android's "delivering" notification.
 * Web app: the browser's location while the page is open.
 * The server answers {tracking: false} when the driver has nothing out for delivery → we stop.
 */
const TASK = 'ops-driver-tracking';

type Point = { lat: number; lng: number; accuracy?: number | null; speed?: number | null; at: string };

const toPoint = (l: Location.LocationObject | GeolocationPosition): Point => ({
  lat: l.coords.latitude,
  lng: l.coords.longitude,
  accuracy: l.coords.accuracy ?? null,
  speed: l.coords.speed != null && l.coords.speed >= 0 ? Math.round(l.coords.speed * 3.6) : null, // m/s → km/h
  at: new Date(l.timestamp).toISOString(),
});

async function send(points: Point[]): Promise<boolean> {
  if (!points.length) return true;
  try {
    const res = await api<{ tracking: boolean }>('/ops/location', { body: { points: points.slice(-50) } });
    return res.tracking !== false;
  } catch {
    return true; // no signal: keep trying with the next points
  }
}

if (Platform.OS !== 'web') {
  TaskManager.defineTask<{ locations: Location.LocationObject[] }>(TASK, async ({ data, error }) => {
    if (error || !data?.locations?.length) return;
    const keepGoing = await send(data.locations.map(toPoint));
    if (!keepGoing) await stopTracking();
  });
}

// ---- web ----
let watchId: number | null = null;
let flushTimer: ReturnType<typeof setInterval> | null = null;
let buffer: Point[] = [];

export async function isTracking(): Promise<boolean> {
  if (Platform.OS === 'web') return watchId !== null;
  return Location.hasStartedLocationUpdatesAsync(TASK).catch(() => false);
}

/** Returns a message when the location permission is missing, otherwise null. */
export async function startTracking(intervalSec: number): Promise<string | null> {
  if (await isTracking()) return null;

  if (Platform.OS === 'web') {
    if (!('geolocation' in navigator)) return t('المتصفح لا يدعم تحديد الموقع', 'This browser cannot share the location');
    watchId = navigator.geolocation.watchPosition(
      (pos) => buffer.push(toPoint(pos)),
      () => {},
      { enableHighAccuracy: true, maximumAge: 10000 }
    );
    flushTimer = setInterval(async () => {
      const points = buffer;
      buffer = [];
      if (!(await send(points))) await stopTracking();
    }, intervalSec * 1000);
    return null;
  }

  const fg = await Location.requestForegroundPermissionsAsync();
  if (fg.status !== 'granted') return t('اسمح للتطبيق بالوصول إلى الموقع حتى تعرف الإدارة مكان التوصيل', 'Allow the app to use your location so the management can follow the delivery');
  const bg = await Location.requestBackgroundPermissionsAsync();
  if (bg.status !== 'granted') return t('اختر "السماح طوال الوقت" للموقع حتى يعمل التتبع والتطبيق في الخلفية', 'Choose "Allow all the time" for location so tracking works in the background');

  await Location.startLocationUpdatesAsync(TASK, {
    accuracy: Location.Accuracy.High,
    timeInterval: intervalSec * 1000,
    distanceInterval: 15,
    deferredUpdatesInterval: intervalSec * 1000,
    showsBackgroundLocationIndicator: true,
    pausesUpdatesAutomatically: false,
    foregroundService: {
      notificationTitle: t('جارٍ توصيل طلب', 'Delivering an order'),
      notificationBody: t('موقعك يُشارك مع الإدارة أثناء التوصيل فقط', 'Your location is shared with the management only while delivering'),
      notificationColor: '#5A1F82',
    },
  });
  return null;
}

export async function stopTracking(): Promise<void> {
  if (Platform.OS === 'web') {
    if (watchId !== null) navigator.geolocation.clearWatch(watchId);
    if (flushTimer) clearInterval(flushTimer);
    watchId = null;
    flushTimer = null;
    buffer = [];
    return;
  }
  if (await Location.hasStartedLocationUpdatesAsync(TASK).catch(() => false)) {
    await Location.stopLocationUpdatesAsync(TASK);
  }
}
